import { useState, useEffect, useCallback } from "react";
import { Page, TitleConfig, DeviceSpecificSettings } from "../types/page";
import { 
  getInitialPages, 
  getInitialTitleConfig, 
  getInitialRotationInterval, 
  getInitialAutoScroll,
  getInitialDeviceSpecificSettings
} from "../contexts/pageInitialState";
import { loadSettingsFromSupabase, parseSettings, saveSetting } from "../contexts/pageSettingsStorage";
import { toast } from "@/components/ui/use-toast";
import { useIsMobile } from "./use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { fetchDefaultPrayerTimes } from "@/utils/defaultSettingsUtils";

export function usePageContextState() {
  // Initialize states with default values
  const [pages, setPages] = useState<Page[]>([]);
  const [titleConfig, setTitleConfig] = useState<TitleConfig>(getInitialTitleConfig());
  const [deviceSpecificSettings, setDeviceSpecificSettings] = useState<DeviceSpecificSettings>(getInitialDeviceSpecificSettings());
  const [rotationInterval, setRotationInterval] = useState<number>(5);
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  
  // Additional states
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [isHebrew, setIsHebrew] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [lastPrayerTimeUpdate, setLastPrayerTimeUpdate] = useState<string>('');
  const [lastSettingsUpdate, setLastSettingsUpdate] = useState<Date>(new Date());

  // Set up auth state listener
  useEffect(() => {
    console.log("Setting up auth state listener");
    
    // First grab the current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        console.log("Found existing session:", session.user.id);
        setCurrentUser(session.user);
      } else {
        console.log("No existing session found");
        setCurrentUser(null);
      }
    });
    
    // Then listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event, session?.user?.id);
        if (session?.user) {
          setCurrentUser(session.user);
        } else {
          setCurrentUser(null);
        }
      }
    );
    
    return () => subscription.unsubscribe();
  }, []);

  // Function to toggle language
  const toggleLanguage = () => {
    setIsHebrew(prev => !prev);
  };

  // Effect to watch for prayer times updates in local storage 
  // This serves as a trigger to reload prayer times data
  useEffect(() => {
    try {
      const handleStorage = () => {
        const timestamp = localStorage.getItem('prayer-times-last-save');
        if (timestamp && timestamp !== lastPrayerTimeUpdate) {
          setLastPrayerTimeUpdate(timestamp);
          // This will trigger the loadSettings effect to run again
        }
      };
      
      window.addEventListener('storage', handleStorage);
      
      // Check on mount too
      handleStorage();
      
      return () => window.removeEventListener('storage', handleStorage);
    } catch (e) {
      console.log('Unable to set up localStorage listener', e);
    }
  }, [lastPrayerTimeUpdate]);

  // Set up real-time subscription to user_settings and app_settings table
  useEffect(() => {
    if (typeof window === 'undefined') {
      return; // Don't run in SSR environment
    }
    
    try {
      // Create a channel to listen for real-time updates
      const channel = supabase.channel('settings-changes')
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'user_settings',
            filter: currentUser?.id ? `user_id=eq.${currentUser.id}` : undefined
          }, 
          (payload) => {
            console.log('User settings changed:', payload);
            setLastSettingsUpdate(new Date()); // Trigger a reload
          }
        )
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'app_settings'
          }, 
          (payload) => {
            console.log('App settings changed:', payload);
            setLastSettingsUpdate(new Date()); // Trigger a reload
          }
        )
        .subscribe((status) => {
          console.log('Realtime subscription status:', status);
        });

      // Cleanup on unmount
      return () => {
        channel.unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up realtime subscription:', error);
      // Don't throw an error, just log it - this prevents app from crashing
    }
  }, [currentUser]);

  // Load settings when the user changes or on initial load or prayerTimeUpdate changes
  useEffect(() => {
    async function loadSettings() {
      console.log("Loading settings, currentUser:", currentUser?.id);
      setIsLoading(true);
      
      try {
        const settingsData = await loadSettingsFromSupabase();
        console.log("Settings data loaded:", settingsData);

        if (!settingsData || settingsData.length === 0) {
          console.log("No settings found in database, using default values");
          setPages(getInitialPages());
          setIsLoading(false);
          return;
        }

        // Parse settings
        const { 
          pages: parsedPages, 
          titleConfig: parsedTitleConfig, 
          deviceSpecificSettings: parsedDeviceSpecificSettings,
          rotationInterval: parsedRotationInterval, 
          autoScroll: parsedAutoScroll 
        } = parseSettings(settingsData, {
          getInitialPages,
          getInitialTitleConfig,
          getInitialDeviceSpecificSettings,
          getInitialRotationInterval,
          getInitialAutoScroll
        });
        
        // Directly fetch prayer times data from the user_settings table
        let prayerTimesData = null;
        
        if (currentUser?.id) {
          console.log("Fetching user-specific prayer times for:", currentUser.id);
          try {
            // Use select() instead of single() to avoid 406 errors
            const { data, error } = await supabase
              .from('user_settings')
              .select('settings_value')
              .eq('user_id', currentUser.id)
              .eq('settings_key', 'prayer_times_data');
            
            if (data && data.length > 0 && !error) {
              prayerTimesData = data[0].settings_value;
              console.log("Found user-specific prayer times data");
            } else {
              console.log("No user-specific prayer times data found, attempting to load default");
              // Try to load default prayer times
              const defaultTimes = await fetchDefaultPrayerTimes();
              if (defaultTimes && defaultTimes.length > 0) {
                prayerTimesData = defaultTimes;
                
                // Save these default times to the user
                await saveSetting('prayer_times_data', defaultTimes);
                console.log("Saved default prayer times to user");
              }
            }
          } catch (prayerTimesError) {
            console.log("Error fetching prayer times data:", prayerTimesError);
            // Continue with the rest of the function
          }
        }
        
        // Update prayer times pages with user-specific data if available
        if (prayerTimesData) {
          const updatedPages = parsedPages.map(page => {
            if (page.type === 'prayer-times') {
              const updatedContent = {
                ...page.content,
                ...prayerTimesData
              };
              
              return {
                ...page,
                content: updatedContent
              };
            }
            return page;
          });
          
          setPages(updatedPages);
        } else {
          setPages(parsedPages);
        }
        
        setTitleConfig(parsedTitleConfig);
        setDeviceSpecificSettings(parsedDeviceSpecificSettings);
        setRotationInterval(parsedRotationInterval);
        setAutoScroll(parsedAutoScroll);
      } catch (error) {
        console.error("Error loading settings:", error);
        toast({
          variant: "destructive",
          title: "Error loading settings",
          description: "Failed to load settings from database. Using default values."
        });
        
        // Fall back to default values
        setPages(getInitialPages());
      } finally {
        setIsLoading(false);
      }
    }

    loadSettings();
  }, [currentUser, lastPrayerTimeUpdate, lastSettingsUpdate]); // Re-load settings when settings are updated

  // Save pages to database whenever they change
  useEffect(() => {
    if (isLoading) return; // Don't save during initial load
    
    const savePages = async () => {
      try {
        await saveSetting('pages', pages);
      } catch (error) {
        console.error("Error saving pages to database:", error);
      }
    };

    if (pages.length > 0) {
      savePages();
    }
  }, [pages, isLoading]);

  // Save title config to database whenever it changes
  useEffect(() => {
    if (isLoading) return; // Don't save during initial load
    
    const saveTitleConfig = async () => {
      try {
        await saveSetting('titleConfig', titleConfig);
      } catch (error) {
        console.error("Error saving title config to database:", error);
      }
    };

    saveTitleConfig();
  }, [titleConfig, isLoading]);

  // Save device specific settings whenever they change
  useEffect(() => {
    if (isLoading) return; // Don't save during initial load
    
    const saveDeviceSettings = async () => {
      try {
        await saveSetting('deviceSpecificSettings', deviceSpecificSettings);
      } catch (error) {
        console.error("Error saving device specific settings to database:", error);
      }
    };

    saveDeviceSettings();
  }, [deviceSpecificSettings, isLoading]);

  // Save rotation interval whenever it changes
  useEffect(() => {
    if (isLoading) return;
    
    const saveRotationInterval = async () => {
      try {
        await saveSetting('rotationInterval', rotationInterval);
      } catch (error) {
        console.error("Error saving rotation interval to database:", error);
      }
    };

    saveRotationInterval();
  }, [rotationInterval, isLoading]);

  // Save auto scroll whenever it changes
  useEffect(() => {
    if (isLoading) return;
    
    const saveAutoScroll = async () => {
      try {
        await saveSetting('autoScroll', autoScroll);
      } catch (error) {
        console.error("Error saving auto scroll to database:", error);
      }
    };

    saveAutoScroll();
  }, [autoScroll, isLoading]);

  return {
    pages, 
    setPages, 
    titleConfig, 
    setTitleConfig,
    deviceSpecificSettings,
    setDeviceSpecificSettings,
    rotationInterval, 
    setRotationInterval,
    autoScroll,
    setAutoScroll,
    currentPageIndex,
    setCurrentPageIndex,
    isHebrew,
    toggleLanguage,
    isLoading,
    currentUser
  };
}
